import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { FiLock, FiEye, FiEyeOff, FiCheck, FiUser } from 'react-icons/fi';
import { authAPI } from '../services/api';
import Footer from '../components/Footer';
import theme from '../styles/theme';

// Schema de validação
const senhaSchema = yup.object({
  senha: yup
    .string()
    .required('Nova senha é obrigatória')
    .min(8, 'Senha deve ter pelo menos 8 caracteres')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/,
      'Senha deve conter: 1 letra minúscula, 1 maiúscula, 1 número e 1 caractere especial'
    ),
  confirmarSenha: yup
    .string()
    .required('Confirmação de senha é obrigatória')
    .oneOf([yup.ref('senha')], 'As senhas devem ser iguais'),
});

// Componentes estilizados
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[4]};
`;

const Card = styled.div`
  background: ${theme.colors.background.card};
  border-radius: ${theme.borderRadius['2xl']};
  box-shadow: ${theme.shadows['2xl']};
  padding: ${theme.spacing[8]};
  width: 100%;
  max-width: 450px;
  border: 1px solid ${theme.colors.border.primary};
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[2]} 0;
  text-align: center;
`;

const Subtitle = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  text-align: center;
  margin: 0 0 ${theme.spacing[8]} 0;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[10]} ${theme.spacing[3]} ${theme.spacing[10]};
  background: ${theme.colors.background.primary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Icon = styled.div`
  position: absolute;
  left: ${theme.spacing[3]};
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.lg};
  z-index: 1;
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: ${theme.spacing[3]};
  background: none;
  border: none;
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.lg};
  cursor: pointer;
  padding: ${theme.spacing[1]};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;

  &:hover {
    color: ${theme.colors.text.secondary};
  }
`;

const ErrorMessage = styled.span`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.sm};
  margin-top: ${theme.spacing[1]};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.success[600]};
  font-size: ${theme.fontSizes.sm};
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: ${theme.spacing[3]};
  background: ${theme.colors.primary[600]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[2]};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[700]};
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid transparent;
  border-top: 2px solid currentColor;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const InfoBox = styled.div`
  background: ${theme.colors.primary[500]}10;
  border: 1px solid ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
`;

const RedefinirSenha = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [userData, setUserData] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(senhaSchema),
  });

  // Buscar dados do usuário pelo UUID
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const response = await authAPI.buscarUsuarioPorUuid(uuid);
        if (response.data.success) {
          setUserData(response.data.data);
        } else {
          setError('Link inválido ou expirado');
        }
      } catch (err) {
        console.error('Erro ao buscar dados do usuário:', err);
        setError('Erro ao carregar dados do usuário');
      } finally {
        setLoadingData(false);
      }
    };

    if (uuid) {
      fetchUserData();
    } else {
      setError('UUID não fornecido');
      setLoadingData(false);
    }
  }, [uuid]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError(null);

      const response = await authAPI.redefinirSenhaPorUuid(uuid, data.senha);

      if (response.data.success) {
        setSuccess(true);
        
        // Redirecionar para login após 2 segundos
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setError(response.data.error || 'Erro ao redefinir senha');
      }
    } catch (err) {
      console.error('Erro ao redefinir senha:', err);
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else {
        setError('Erro ao conectar com o servidor');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <ContentWrapper>
        <Card>
          <Title>Redefinir Senha</Title>
          <Subtitle>
            Olá! Por favor, defina uma nova senha para sua conta.
          </Subtitle>

          {loadingData ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <p>Carregando...</p>
            </div>
          ) : success ? (
            <SuccessMessage>
              <FiCheck />
              Senha redefinida com sucesso! Redirecionando para o login...
            </SuccessMessage>
          ) : userData ? (
            <Form onSubmit={handleSubmit(onSubmit)}>
              {error && <ErrorMessage>{error}</ErrorMessage>}

              <InfoBox style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <FiUser />
                <span><strong>Email:</strong> {userData.email}</span>
              </InfoBox>

              <FormGroup>
                <Label htmlFor="senha">Nova Senha</Label>
                <InputContainer>
                  <Icon>
                    <FiLock />
                  </Icon>
                  <Input
                    id="senha"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Digite sua nova senha"
                    {...register('senha')}
                    disabled={loading}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={loading}
                  >
                    {showPassword ? <FiEyeOff /> : <FiEye />}
                  </PasswordToggle>
                </InputContainer>
                {errors.senha && (
                  <ErrorMessage>{errors.senha.message}</ErrorMessage>
                )}
              </FormGroup>

              <FormGroup>
                <Label htmlFor="confirmarSenha">Confirmar Nova Senha</Label>
                <InputContainer>
                  <Icon>
                    <FiLock />
                  </Icon>
                  <Input
                    id="confirmarSenha"
                    type={showConfirmPassword ? 'text' : 'password'}
                    placeholder="Confirme sua nova senha"
                    {...register('confirmarSenha')}
                    disabled={loading}
                  />
                  <PasswordToggle
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    disabled={loading}
                  >
                    {showConfirmPassword ? <FiEyeOff /> : <FiEye />}
                  </PasswordToggle>
                </InputContainer>
                {errors.confirmarSenha && (
                  <ErrorMessage>{errors.confirmarSenha.message}</ErrorMessage>
                )}
              </FormGroup>

              <SubmitButton type="submit" disabled={loading}>
                {loading ? (
                  <>
                    <LoadingSpinner />
                    Salvando...
                  </>
                ) : (
                  'Redefinir Senha'
                )}
              </SubmitButton>
            </Form>
          ) : (
            <ErrorMessage>Link inválido ou expirado. Por favor, solicite um novo link.</ErrorMessage>
          )}
        </Card>
      </ContentWrapper>
      <Footer />
    </Container>
  );
};

export default RedefinirSenha;

